// Parser für die Sucheingabe (docs/API.md 3.8): Freitext, Phrasen "…", Ausschlüsse -wort und Inline-Filter
// (from:, in:, has:, before:, after:, during:, mentions:, pinned:). Rein und ohne React – gut testbar.
//
// Entscheidung: Der Server parst die Inline-Filter selbst (Vertrag 3.8). Der Client sendet deshalb die komplette,
// normalisierte Eingabe als `q` und nutzt den Parser nur für Validierung, Hervorhebung (Chips) und
// Autovervollständigung. Zusätzlich gehen nur Bereich (serverId/channelId), sort, offset und limit mit.
import { SEARCH_HAS_TYPES, type SearchHasType, type SearchParams, type SearchSort } from '../api';

export const FILTER_KEYS = ['from', 'in', 'has', 'before', 'after', 'during', 'mentions', 'pinned'] as const;
export type FilterKey = (typeof FILTER_KEYS)[number];

export const isFilterKey = (s: string): s is FilterKey => (FILTER_KEYS as readonly string[]).includes(s);

const DATE_KEYS: readonly FilterKey[] = ['before', 'after', 'during'];
export const isDateKey = (k: FilterKey): boolean => DATE_KEYS.includes(k);

export type TokenKind = 'word' | 'phrase' | 'exclude' | 'filter';

export interface SearchToken {
  kind: TokenKind;
  /** Exakter Text in der Eingabe (inkl. Schlüssel, Minus, Anführungszeichen). */
  raw: string;
  /** Normalisierter Wert (ohne Anführungszeichen; bei in: ohne `#`/`<#…>`, bei from:/mentions: ohne `@`/`<@…>`). */
  value: string;
  /** Position in der Eingabe: [start, end). */
  start: number;
  end: number;
  /** Nur Filter. */
  key?: FilterKey;
  /** Wert stand in Anführungszeichen. */
  quoted?: boolean;
  /** Schließendes Anführungszeichen vorhanden (nur wenn quoted). */
  closed?: boolean;
}

export interface SearchFilters {
  from: string[];
  in: string[];
  has: SearchHasType[];
  before?: string;
  after?: string;
  during?: string;
  mentions: string[];
  pinned?: boolean;
}

export interface ParseError {
  token: SearchToken;
  message: string;
}

export interface ParsedQuery {
  /** Ursprüngliche Eingabe. */
  input: string;
  tokens: SearchToken[];
  /** Freitext inkl. Phrasen und Ausschlüssen (ohne Filter), mit einfachen Leerzeichen verbunden. */
  text: string;
  filters: SearchFilters;
  errors: ParseError[];
}

const isSpace = (c: string | undefined) => c !== undefined && /\s/.test(c);

function nextSpace(input: string, from: number): number {
  let i = from;
  while (i < input.length && !isSpace(input[i])) i++;
  return i;
}

/** Wert in Anführungszeichen ab `quoteAt` lesen (bis zum nächsten `"` oder Ende). */
function readQuoted(input: string, quoteAt: number): { value: string; end: number; closed: boolean } {
  const close = input.indexOf('"', quoteAt + 1);
  if (close === -1) return { value: input.slice(quoteAt + 1), end: input.length, closed: false };
  return { value: input.slice(quoteAt + 1, close), end: close + 1, closed: true };
}

function normalizeValue(key: FilterKey, value: string): string {
  const v = value.trim();
  if (key === 'in') {
    const ref = /^<#(\d+)>$/.exec(v);
    if (ref) return ref[1];
    return v.replace(/^#/, '');
  }
  if (key === 'from' || key === 'mentions') {
    const ref = /^<@!?(\d+)>$/.exec(v);
    if (ref) return ref[1];
    return v.replace(/^@/, '');
  }
  if (key === 'has' || key === 'pinned') return v.toLowerCase();
  return v;
}

/** Eingabe in Tokens mit Positionen zerlegen. */
export function tokenize(input: string): SearchToken[] {
  const tokens: SearchToken[] = [];
  const n = input.length;
  let i = 0;
  while (i < n) {
    if (isSpace(input[i])) {
      i++;
      continue;
    }
    const start = i;
    const neg = input[i] === '-' && i + 1 < n && !isSpace(input[i + 1]);
    if (neg) i++;

    if (input[i] === '"') {
      const q = readQuoted(input, i);
      tokens.push({ kind: neg ? 'exclude' : 'phrase', raw: input.slice(start, q.end), value: q.value, start, end: q.end, quoted: true, closed: q.closed });
      i = q.end;
      continue;
    }

    const keyMatch = neg ? null : /^([A-Za-z]+):/.exec(input.slice(i, i + 12));
    const key = keyMatch?.[1].toLowerCase();
    if (keyMatch && key && isFilterKey(key)) {
      const valueAt = i + keyMatch[0].length;
      if (input[valueAt] === '"') {
        const q = readQuoted(input, valueAt);
        tokens.push({ kind: 'filter', key, raw: input.slice(start, q.end), value: normalizeValue(key, q.value), start, end: q.end, quoted: true, closed: q.closed });
        i = q.end;
      } else {
        const end = nextSpace(input, valueAt);
        tokens.push({ kind: 'filter', key, raw: input.slice(start, end), value: normalizeValue(key, input.slice(valueAt, end)), start, end, quoted: false });
        i = end;
      }
      continue;
    }

    const end = nextSpace(input, i);
    tokens.push({ kind: neg ? 'exclude' : 'word', raw: input.slice(start, end), value: input.slice(i, end), start, end });
    i = end;
  }
  return tokens;
}

/** Gültiges Kalenderdatum im Format YYYY-MM-DD? */
export function isValidDate(value: string): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const date = new Date(Date.UTC(y, mo - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === mo - 1 && date.getUTCDate() === d;
}

const isHasType = (v: string): v is SearchHasType => (SEARCH_HAS_TYPES as readonly string[]).includes(v);

/** Eingabe vollständig parsen und validieren. */
export function parseQuery(input: string): ParsedQuery {
  const tokens = tokenize(input);
  const filters: SearchFilters = { from: [], in: [], has: [], mentions: [] };
  const errors: ParseError[] = [];
  const text: string[] = [];
  let afterToken: SearchToken | undefined;

  for (const t of tokens) {
    if (t.quoted && !t.closed) errors.push({ token: t, message: 'Anführungszeichen nicht geschlossen.' });
    if (t.kind === 'word') {
      text.push(t.value);
      continue;
    }
    if (t.kind === 'phrase') {
      if (t.value.trim()) text.push(`"${t.value}"`);
      continue;
    }
    if (t.kind === 'exclude') {
      if (t.quoted) {
        if (t.value.trim()) text.push(`-"${t.value}"`);
      } else text.push(`-${t.value}`);
      continue;
    }
    const key = t.key as FilterKey;
    const v = t.value;
    if (!v) {
      errors.push({ token: t, message: `Filter „${key}:" braucht einen Wert.` });
      continue;
    }
    switch (key) {
      case 'from':
      case 'in':
      case 'mentions':
        filters[key].push(v);
        break;
      case 'has':
        if (isHasType(v)) {
          if (!filters.has.includes(v)) filters.has.push(v);
        } else errors.push({ token: t, message: `Unbekannter Typ „${v}" – erlaubt: ${SEARCH_HAS_TYPES.join(', ')}.` });
        break;
      case 'before':
      case 'after':
      case 'during':
        if (isValidDate(v)) {
          filters[key] = v;
          if (key === 'after') afterToken = t;
        } else errors.push({ token: t, message: `Ungültiges Datum „${v}" – Format JJJJ-MM-TT.` });
        break;
      case 'pinned':
        if (v === 'true' || v === 'false') filters.pinned = v === 'true';
        else errors.push({ token: t, message: '„pinned:" erlaubt nur true oder false.' });
        break;
    }
  }
  if (filters.before && filters.after && afterToken && filters.after >= filters.before) {
    errors.push({ token: afterToken, message: '„after:" muss vor „before:" liegen.' });
  }
  return { input, tokens, text: text.join(' '), filters, errors };
}

/** Was wird an der Cursorposition gerade getippt? (Grundlage der Autovervollständigung) */
export type ActiveToken =
  /** Leere Stelle oder Wortanfang → Filterschlüssel vorschlagen. */
  | { kind: 'key'; prefix: string; start: number; end: number }
  /** Wert eines Filters → passende Werte vorschlagen. */
  | { kind: 'value'; key: FilterKey; prefix: string; start: number; end: number }
  /** Phrase/Ausschluss → keine Vorschläge. */
  | { kind: 'none'; prefix: ''; start: number; end: number };

export function activeTokenAt(input: string, cursor: number): ActiveToken {
  const c = Math.max(0, Math.min(cursor, input.length));
  const token = tokenize(input).find((t) => t.start < c && c <= t.end);
  if (!token) return { kind: 'key', prefix: '', start: c, end: c };
  if (token.kind === 'phrase' || token.kind === 'exclude') return { kind: 'none', prefix: '', start: token.start, end: token.end };
  if (token.kind === 'word') return { kind: 'key', prefix: input.slice(token.start, c), start: token.start, end: token.end };
  const key = token.key as FilterKey;
  const valueStart = token.start + key.length + 1 + (token.quoted ? 1 : 0);
  const valueEnd = token.end - (token.quoted && token.closed ? 1 : 0);
  const raw = c <= valueStart ? '' : input.slice(valueStart, Math.min(c, valueEnd));
  const prefix = raw.replace(key === 'in' ? /^<?#/ : key === 'from' || key === 'mentions' ? /^<?@/ : /^$/, '');
  return { kind: 'value', key, prefix, start: token.start, end: token.end };
}

/** Filter als Text formatieren; Werte mit Leerzeichen kommen in Anführungszeichen (`in:"mein kanal"`). */
export function formatFilter(key: FilterKey, value: string): string {
  const v = value.replace(/"/g, '').trim();
  return /\s/.test(v) ? `${key}:"${v}"` : `${key}:${v}`;
}

/**
 * Bereich [start, end) durch `text` ersetzen. Endet `text` nicht auf `:` (z. B. `from:`), folgt ein Leerzeichen,
 * damit direkt weitergetippt werden kann. Liefert neue Eingabe und Cursorposition.
 */
export function replaceToken(input: string, token: { start: number; end: number }, text: string): { input: string; cursor: number } {
  const before = input.slice(0, token.start);
  let after = input.slice(token.end);
  const open = text.endsWith(':');
  if (!open) after = after.startsWith(' ') ? after : ` ${after}`;
  const cursor = before.length + text.length + (open ? 0 : 1);
  return { input: before + text + after, cursor };
}

/** Token aus der Eingabe entfernen (Chip „×"). */
export function removeToken(input: string, token: { start: number; end: number }): string {
  return `${input.slice(0, token.start)} ${input.slice(token.end)}`.replace(/\s{2,}/g, ' ').trim();
}

export interface SearchScope {
  serverId?: string | null;
  channelId?: string | null;
  sort?: SearchSort;
  offset?: number;
  limit?: number;
}

export const SEARCH_LIMIT = 25;

/**
 * Token für `q` (Vertrag 3.8): Filter normalisiert (`from:@anna` → `from:anna`, `from:<@123>` → `from:123`,
 * `in:#allgemein` → `in:allgemein`, `in:<#5>` → `in:5`, `has:Image` → `has:image`), Wörter/Phrasen/Ausschlüsse roh.
 * Filter ohne Wert bleiben roh (Server meldet/ignoriert sie).
 */
function serverToken(t: SearchToken): string {
  if (t.kind !== 'filter' || !t.key || !t.value) return t.raw;
  return formatFilter(t.key, t.value);
}

/**
 * Parameter für `GET /search/messages`. `q` = normalisierte komplette Eingabe (Server parst die Inline-Filter).
 * Enthält die Eingabe `in:`-Filter, wird ein Kanal-Bereich nicht zusätzlich gesendet (sonst UND-Verknüpfung,
 * die fast immer leer wäre); der Server-Bereich bleibt.
 */
export function toSearchParams(parsed: ParsedQuery, scope: SearchScope = {}): SearchParams {
  const q = parsed.tokens.map(serverToken).join(' ');
  const channelId = scope.channelId && parsed.filters.in.length === 0 ? [scope.channelId] : undefined;
  return {
    q: q || undefined,
    serverId: scope.serverId || undefined,
    channelId,
    sort: scope.sort ?? 'newest',
    offset: scope.offset ?? 0,
    limit: Math.min(Math.max(1, scope.limit ?? SEARCH_LIMIT), SEARCH_LIMIT),
  };
}
